import { useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import BillingService from '@Helpers/URLHelpers/BillingService';
import { useReduxSubscription } from '@Hooks/reduxSubscriptionHook/useReduxSubscription';
import SnackbarManager from '@CommonFunctionalDisplay/GlobalSnackbar/SnackbarManager';
import './PaymentFailedBanner.scss';

const billingService = new BillingService();

const isSuccessfulResponse = (response) => {
    if (response?.isSuccess === true) return true;
    if (!response || response.error) return false;
    return typeof response.message === 'string' && response.message.trim().toLowerCase() === 'success';
};

export default function PaymentFailedBanner({ subscription, isOwner }) {
    const [restarting, setRestarting] = useState(false);
    const { setCurrentSubscription } = useReduxSubscription();

    if (subscription?.status !== 'HALTED') {
        return null;
    }

    const className = 'paymentFailedBanner';

    const handleRestart = async () => {
        setRestarting(true);
        try {
            const data = await billingService.restartSubscription(subscription.orgId);
            if (isSuccessfulResponse(data)) {
                if (data?.data) setCurrentSubscription(data.data);
                const shortUrl = data?.data?.shortUrl;
                if (shortUrl) {
                    window.open(shortUrl, '_blank', 'noopener,noreferrer');
                }
                SnackbarManager.success('New subscription started — complete payment to activate it.');
            } else {
                SnackbarManager.error(data?.message || 'Failed to start a new subscription');
            }
        } catch (_) {
            SnackbarManager.error('Failed to start a new subscription');
        } finally {
            setRestarting(false);
        }
    };

    return (
        <div className={className}>
            <AlertTriangle size={16} className={className + '__icon'} />
            <div className={className + '__text'}>
                <span className={className + '__title'}>Payment failed</span>
                <span className={className + '__message'}>
                    We were unable to process your last payment. Start a new subscription to restore access.
                    {!isOwner && ' Contact your organization owner to restart the subscription.'}
                </span>
            </div>
            {isOwner && (
                <button
                    type="button"
                    className={className + '__action'}
                    onClick={handleRestart}
                    disabled={restarting}
                >
                    {restarting ? 'Starting…' : 'Try Again'}
                </button>
            )}
        </div>
    );
}
